import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import '@/App.css';

import TestModeIndicator from '@/components/TestModeIndicator'; // SANDBOX MODE
import SplashScreen from '@/pages/SplashScreen';
import NotFound from '@/components/NotFound';
import AdminShield from '@/components/AdminShield';

import Landing from '@/pages/Landing';
import ClientLogin from '@/pages/client/Login';
import ClientForgotPassword from '@/pages/client/ForgotPassword';
import ClientResetPassword from '@/pages/client/ResetPassword';
import ClientDashboard from '@/pages/client/Dashboard';
import ClientPlans from '@/pages/client/Plans';
import ClientVehicle from '@/pages/client/Vehicle';
import ClientSchedule from '@/pages/client/Schedule';
import ClientHistory from '@/pages/client/History';
import ClientPaymentSuccess from '@/pages/client/PaymentSuccess';
import ClientCompleteProfile from '@/pages/client/CompleteProfile';
import ClientTerms from '@/pages/client/Terms';
import ClientPayment from '@/pages/client/Payment';
import ClientAppointments from '@/pages/client/Appointments';
import ClientProviders from '@/pages/client/Providers';
import ClientProfileHub from '@/pages/client/ProfileHub';
import ClientPersonalData from '@/pages/client/PersonalData';
import ClientHelpCenter from '@/pages/client/HelpCenter';
import ClientPlanDetails from '@/pages/client/PlanDetails';
import ClientMigratePlan from '@/pages/client/MigratePlan';
import ClientMigrateSuccess from '@/pages/client/MigrateSuccess';
import ClientPlanChangeSuccess from '@/pages/client/PlanChangeSuccess';
import OnboardingVehicle from '@/pages/client/onboarding/OnboardingVehicle';
import OnboardingSubscriptionData from '@/pages/client/onboarding/OnboardingSubscriptionData';
import OnboardingPlan from '@/pages/client/onboarding/OnboardingPlan';
import AppointmentAction from '@/pages/client/AppointmentAction';
import ClientSupport from '@/pages/client/Support';

import ProviderLogin from '@/pages/provider/Login';
import ProviderRegister from '@/pages/provider/Register';
import ProviderOnboarding from '@/pages/provider/Onboarding';
import ProviderAppointments from '@/pages/provider/Appointments';
import ProviderLayout from '@/components/provider/ProviderLayout';

import AdminLogin from '@/pages/admin/Login';
import AdminDashboard from '@/pages/admin/Dashboard';
import AdminClients from '@/pages/admin/Clients';
import AdminClientDetail from '@/pages/admin/ClientDetail';
import AdminProviders from '@/pages/admin/Providers';
import AdminProviderDetail from '@/pages/admin/ProviderDetail';
import AdminTransfers from '@/pages/admin/Transfers';
import AdminFraud from '@/pages/admin/Fraud';
import AdminSettings from '@/pages/admin/Settings';
import AdminPlans from '@/pages/admin/Plans';
import AdminSubscriptions from '@/pages/admin/Subscriptions';
import AdminFinancial from '@/pages/admin/Financial';
import AdminAppointments from '@/pages/admin/Appointments';
import AdminReviews from '@/pages/admin/Reviews';
import AdminSupport from '@/pages/admin/Support';
import AdminTerms from '@/pages/admin/Terms';
import AdminDisputes from '@/pages/admin/Disputes';
import AdminFinancialSettings from '@/pages/admin/FinancialSettings';
import AdminPayoutRequests from '@/pages/admin/PayoutRequests';
import AdminFinancialDashboard from '@/pages/admin/FinancialDashboard';
import AdminProviderScores from '@/pages/admin/ProviderScores';
import AdminChangePassword from '@/pages/admin/ChangePassword';

import TermsClient from '@/pages/public/TermsClient';
import TermsProvider from '@/pages/public/TermsProvider';
import DeleteAccount from '@/pages/public/DeleteAccount';
import PrivacyPolicy from '@/pages/public/PrivacyPolicy';

import AuthCallback from '@/components/AuthCallback';
import MobileBottomNav, { BOTTOM_NAV_ROUTES } from '@/components/client/MobileBottomNav';
import ClientDesktopNav from '@/components/client/ClientDesktopNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = async () => {
    const token = localStorage.getItem('token');
    
    // Tentar com token JWT primeiro
    if (token) {
      try {
        const res = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(res.data.user);
        return true;
      } catch (error) {
        localStorage.removeItem('token');
      }
    }
    
    // Fallback: tentar com cookie session_token (Google OAuth)
    try {
      const res = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(res.data.user);
      return true;
    } catch (error) {
      // Não autenticado
      return false;
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      await fetchUser();
      setLoading(false);
    };
    
    checkAuth();
  }, []);

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const updateUser = (updatedUserData) => {
    setUser(prevUser => ({ ...prevUser, ...updatedUserData }));
  };

  // Função para recarregar dados do usuário do servidor
  const refreshUser = async () => {
    await fetchUser();
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Carregando...</div>;
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, updateUser, refreshUser }}>
      {/* SANDBOX MODE: Indicador visual */}
      <TestModeIndicator />
      
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster />
    </AuthContext.Provider>
  );
}

function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();

  // ============== CAPACITOR DEEP LINK HANDLER ==============
  // Recebe URLs entregues ao app pelo SO Android/iOS através de:
  //  (a) Android App Links HTTPS verificados (intent-filter autoVerify=true
  //      em AndroidManifest.xml + /.well-known/assetlinks.json em jatoo.app.br)
  //  (b) iOS Universal Links (Associated Domains entitlement + AASA)
  //
  // O retorno do Stripe Checkout (https://jatoo.app.br/client/payment-success?...)
  // é entregue aqui pelo SO. Convertemos a URL para uma rota interna do React
  // Router (host é descartado — estamos dentro do bundle local, não fazendo
  // fetch para jatoo.app.br) e fechamos a in-app browser se aberta.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let listenerHandle;
    (async () => {
      listenerHandle = await CapacitorApp.addListener('appUrlOpen', async (event) => {
        try {
          // event.url: "https://jatoo.app.br/client/payment-success?session_id=cs_test_..."
          const url = new URL(event.url);
          const pathWithQuery = `${url.pathname}${url.search}${url.hash}`;
          // Fechar a in-app browser (Chrome Custom Tab / SFSafariViewController)
          try { await Browser.close(); } catch (_) { /* já fechado */ }
          // Navegar para a rota interna correspondente
          if (pathWithQuery && pathWithQuery !== '/') {
            navigate(pathWithQuery);
          }
        } catch (e) {
          console.error('[appUrlOpen] erro ao processar deep link:', e, event?.url);
        }
      });
    })();

    return () => {
      if (listenerHandle) {
        try { listenerHandle.remove(); } catch (_) { /* noop */ }
      }
    };
  }, [navigate]);

  // CRITICAL: Check URL fragment (not query params) for session_id synchronously
  // This prevents race conditions by processing Google auth BEFORE checking existing session_token
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Pass 10A: padding inferior apenas nas 4 rotas com Bottom Navigation (mobile), para o
  // conteúdo não ficar escondido pela barra fixa. Sem tocar nas páginas.
  const showBottomNav = BOTTOM_NAV_ROUTES.includes(location.pathname);

  return (
    <>
      <div className={showBottomNav ? 'pb-20 md:pb-0 md:pl-60' : ''}>
        <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/splash" element={<SplashScreen />} />
      
      {/* Rota pública de termos */}
      <Route path="/terms/client" element={<TermsClient />} />
      <Route path="/terms/provider" element={<TermsProvider />} />

      {/* Rota pública de exclusão de conta — requisito Google Play */}
      <Route path="/excluir-conta" element={<DeleteAccount />} />

      {/* Rota pública de Política de Privacidade */}
      <Route path="/privacidade" element={<PrivacyPolicy />} />
      
      {/* Rotas principais simplificadas para domínio jatoo.app.br */}
      <Route path="/login" element={<Navigate to="/client/login" replace />} />
      <Route path="/cliente" element={<Navigate to="/client/dashboard" replace />} />
      <Route path="/prestador" element={<Navigate to="/provider/dashboard" replace />} />
      
      <Route path="/client/login" element={<ClientLogin />} />
      <Route path="/client/forgot-password" element={<ClientForgotPassword />} />
      <Route path="/client/reset-password" element={<ClientResetPassword />} />
      <Route path="/client/dashboard" element={<ClientDashboard />} />
      <Route path="/client/plans" element={<ClientPlans />} />
      <Route path="/client/terms" element={<ClientTerms />} />
      <Route path="/client/payment" element={<ClientPayment />} />
      <Route path="/client/vehicle" element={<ClientVehicle />} />
      <Route path="/client/schedule" element={<ClientSchedule />} />
      <Route path="/client/appointments" element={<ClientAppointments />} />
      <Route path="/client/providers" element={<ClientProviders />} />
      <Route path="/client/profile" element={<ClientProfileHub />} />
      <Route path="/client/personal-data" element={<ClientPersonalData />} />
      <Route path="/client/history" element={<ClientHistory />} />
      <Route path="/client/payment-success" element={<ClientPaymentSuccess />} />
      <Route path="/client/complete-profile" element={<ClientCompleteProfile />} />
      <Route path="/client/help" element={<ClientHelpCenter />} />
      <Route path="/client/plan-details" element={<ClientPlanDetails />} />
      <Route path="/client/migrate-plan" element={<ClientMigratePlan />} />
      <Route path="/client/migrate-success" element={<ClientMigrateSuccess />} />
      <Route path="/client/plan-change-success" element={<ClientPlanChangeSuccess />} />
      
      {/* Rotas de Onboarding do Cliente */}
      <Route path="/client/onboarding/vehicle" element={<OnboardingVehicle />} />
      <Route path="/client/onboarding/subscription-data" element={<OnboardingSubscriptionData />} />
      <Route path="/client/onboarding/plan" element={<OnboardingPlan />} />
      
      {/* Rota de ação de agendamento (confirmação via email) */}
      <Route path="/client/appointment/:id/:action" element={<AppointmentAction />} />
      
      {/* Rota de Suporte do Cliente */}
      <Route path="/client/support" element={<ClientSupport />} />
      
      <Route path="/provider/login" element={<ProviderLogin />} />
      <Route path="/provider/register" element={<ProviderRegister />} />
      <Route path="/provider/onboarding" element={<ProviderOnboarding />} />
      <Route path="/provider/appointments" element={<ProviderAppointments />} />
      <Route path="/provider/*" element={<ProviderLayout />} />
      
      {/* ========== ADMIN STEALTH MODE ========== */}
      {/* Rotas públicas de admin bloqueadas - retornam 404 */}
      <Route path="/admin" element={<NotFound />} />
      <Route path="/admin/*" element={<NotFound />} />
      <Route path="/admin-panel" element={<NotFound />} />
      <Route path="/admin-panel/*" element={<NotFound />} />
      <Route path="/dashboard-admin" element={<NotFound />} />
      <Route path="/dashboard-admin/*" element={<NotFound />} />
      <Route path="/control-panel" element={<NotFound />} />
      <Route path="/control-panel/*" element={<NotFound />} />
      <Route path="/painel" element={<NotFound />} />
      <Route path="/painel/*" element={<NotFound />} />
      <Route path="/administrativo" element={<NotFound />} />
      <Route path="/administrativo/*" element={<NotFound />} />
      
      {/* Rota privada do admin - caminho secreto */}
      <Route path="/control-portal-clube/login" element={<AdminLogin />} />
      <Route path="/control-portal-clube" element={<AdminShield><AdminDashboard /></AdminShield>} />
      <Route path="/control-portal-clube/dashboard" element={<AdminShield><AdminDashboard /></AdminShield>} />
      <Route path="/control-portal-clube/clients" element={<AdminShield><AdminClients /></AdminShield>} />
      <Route path="/control-portal-clube/clients/:clientId" element={<AdminShield><AdminClientDetail /></AdminShield>} />
      <Route path="/control-portal-clube/providers" element={<AdminShield><AdminProviders /></AdminShield>} />
      <Route path="/control-portal-clube/providers/:providerId" element={<AdminShield><AdminProviderDetail /></AdminShield>} />
      <Route path="/control-portal-clube/transfers" element={<AdminShield><AdminTransfers /></AdminShield>} />
      <Route path="/control-portal-clube/fraud" element={<AdminShield><AdminFraud /></AdminShield>} />
      <Route path="/control-portal-clube/settings" element={<AdminShield><AdminSettings /></AdminShield>} />
      <Route path="/control-portal-clube/plans" element={<AdminShield><AdminPlans /></AdminShield>} />
      <Route path="/control-portal-clube/subscriptions" element={<AdminShield><AdminSubscriptions /></AdminShield>} />
      <Route path="/control-portal-clube/financial" element={<AdminShield><AdminFinancial /></AdminShield>} />
      <Route path="/control-portal-clube/financial-dashboard" element={<AdminShield><AdminFinancialDashboard /></AdminShield>} />
      <Route path="/control-portal-clube/appointments" element={<AdminShield><AdminAppointments /></AdminShield>} />
      <Route path="/control-portal-clube/reviews" element={<AdminShield><AdminReviews /></AdminShield>} />
      <Route path="/control-portal-clube/support" element={<AdminShield><AdminSupport /></AdminShield>} />
      <Route path="/control-portal-clube/terms" element={<AdminShield><AdminTerms /></AdminShield>} />
      <Route path="/control-portal-clube/disputes" element={<AdminShield><AdminDisputes /></AdminShield>} />
      <Route path="/control-portal-clube/financial-settings" element={<AdminShield><AdminFinancialSettings /></AdminShield>} />
      <Route path="/control-portal-clube/payout-requests" element={<AdminShield><AdminPayoutRequests /></AdminShield>} />
      <Route path="/control-portal-clube/provider-scores" element={<AdminShield><AdminProviderScores /></AdminShield>} />
      <Route path="/control-portal-clube/change-password" element={<AdminShield><AdminChangePassword /></AdminShield>} />
      
      {/* Rota 404 para qualquer outro caminho */}
      <Route path="*" element={<NotFound />} />
    </Routes>
      </div>
      <ClientDesktopNav />
      <MobileBottomNav />
    </>
  );
}

export default App;
